// A full, skinnable control bar rendered in vanilla DOM over a managed Player.
// Everything visual is driven by --havik-* CSS variables (see theme.ts), so a
// customer re-skins it purely with a `theme` object or their own stylesheet.
//
// It speaks only the public Player surface (play/pause/setMuted/…/on), so it has
// no privileged access to engine internals and could equally wrap a BYO player.

import { injectStyles } from './styles';
import { svgIcon, type IconName } from './icons';
import {
  enterVideoFullscreen,
  exitVideoFullscreen,
  videoFullscreenActive,
} from '../core/fullscreen';
import type { Player } from '../managed';
import type { AudioTrackInfo, PlayerState, QualityLevel, TextTrackInfo } from '../core/stats';
import type { PlaybackError } from '../core/errors';

export interface ControlBarOptions {
  /** The `.havik-player` container that wraps the <video>. */
  root: HTMLElement;
  video: HTMLVideoElement;
  player: Player;
  /** Optional corner watermark + status-card logo (logo URL). */
  logoUrl?: string;
  /** Milliseconds of pointer inactivity before the bar auto-hides. Default 3000. */
  autoHideMs?: number;
  /**
   * Branded full-bleed cards for the stopped/pre-play states (ended, fatal
   * error, pre-play, armed-waiting) instead of a frozen frame + dead play
   * button. Default true. Set false if you render your own end/error UI from
   * the player events.
   */
  statusOverlays?: boolean;
  /** Message on the end-of-stream card. Default "This stream has ended". */
  endedMessage?: string;
  /**
   * Message shown INSTEAD of endedMessage when the server says the stream
   * stopped but the match is still running (`endedReason: 'interrupted'` —
   * an ingest outage that outlived the bridge's hold). Telling a viewer
   * mid-match that the stream "has ended" is what this exists to prevent.
   *
   * Default "Reconnecting — please stand by", which matches what the player
   * does WHEN auto-rejoin is on (`rejoinOnLive`, the default, and it needs
   * `liveStateEvents` too). A host that turns rejoin off should pass its own
   * wording here: nothing will re-attach on its own, so the default would
   * promise the viewer a reconnect that never comes.
   */
  interruptedMessage?: string;
  /** Message on the fatal-error card. Default: the error's own message. */
  errorMessage?: string;
}

export interface ControlBarHandle {
  destroy(): void;
}

type MenuKind = 'quality' | 'audio' | 'cc';

const HIDE_AFTER_MS = 3000;

// Terminal error codes get NO Retry button — re-resolving returns the same
// outcome (stream gone / unknown URN / bad key / malformed URN). Everything else
// (network / media / DRM / internal / transient) keeps Retry, since retry()
// re-resolves AND re-attaches and can recover — including refreshing an expired
// signed DRM license URL.
const NON_RETRYABLE_ERROR_CODES = new Set(['GONE', 'NOT_FOUND', 'INVALID_URN', 'UNAUTHORIZED']);

/** Mount the Oddin control bar onto a managed player. Returns a teardown handle. */
export function mountControlBar(opts: ControlBarOptions): ControlBarHandle {
  return new ControlBar(opts);
}

class ControlBar implements ControlBarHandle {
  private readonly root: HTMLElement;
  private readonly video: HTMLVideoElement;
  private readonly player: Player;
  private readonly doc: Document;
  private readonly autoHideMs: number;

  // Elements
  private readonly overlay: HTMLElement;
  private readonly center: HTMLButtonElement;
  private readonly spinner: HTMLElement;
  private readonly bar: HTMLElement;
  private readonly gradient: HTMLElement;
  private readonly seek: HTMLElement;
  private readonly seekBuffered: HTMLElement;
  private readonly seekPlayed: HTMLElement;
  private readonly seekThumb: HTMLElement;
  private readonly playBtn: HTMLButtonElement;
  private readonly muteBtn: HTMLButtonElement;
  private readonly volSlider: HTMLInputElement;
  private readonly live: HTMLElement;
  private readonly liveGo: HTMLButtonElement;
  private readonly time: HTMLElement;
  private readonly ccBtn: HTMLButtonElement;
  private readonly audioBtn: HTMLButtonElement;
  private readonly qualityBtn: HTMLButtonElement;
  private readonly pipBtn: HTMLButtonElement;
  private readonly fsBtn: HTMLButtonElement;
  private readonly menu: HTMLElement;

  // Status card (ended / error / pre-play / waiting) — see syncStatus().
  private statusBox: HTMLElement | null = null;
  private statusKind: 'ended' | 'error' | 'preplay' | 'waiting' | null = null;
  /** Copy currently rendered on the card; part of the dedup key (see
   *  renderStatus) so a same-kind message change still repaints. */
  private statusMsg: string | null = null;
  private hasPlayed = false;
  private lastError: PlaybackError | null = null;
  private readonly logoUrl?: string;
  private readonly statusOverlays: boolean;
  private readonly endedMessage: string;
  private readonly interruptedMessage: string;
  private readonly errorMessage?: string;

  private hideTimer?: ReturnType<typeof setTimeout>;
  private openMenu: MenuKind | null = null;
  private scrubbing = false;
  private readonly offFns: Array<() => void> = [];

  constructor(o: ControlBarOptions) {
    this.root = o.root;
    this.video = o.video;
    this.player = o.player;
    this.doc = o.root.ownerDocument ?? document;
    this.autoHideMs = o.autoHideMs ?? HIDE_AFTER_MS;
    this.logoUrl = o.logoUrl;
    this.statusOverlays = o.statusOverlays !== false;
    this.endedMessage = o.endedMessage ?? 'This stream has ended';
    this.interruptedMessage = o.interruptedMessage ?? 'Reconnecting — please stand by';
    this.errorMessage = o.errorMessage;
    injectStyles(this.doc);

    this.root.classList.add('havik-player');
    if (!this.root.hasAttribute('tabindex')) this.root.tabIndex = 0;
    this.root.setAttribute('role', 'region');
    this.root.setAttribute('aria-label', 'Video player');

    const el = <K extends keyof HTMLElementTagNameMap>(
      tag: K,
      cls: string,
      attrs?: Record<string, string>,
    ): HTMLElementTagNameMap[K] => {
      const node = this.doc.createElement(tag);
      node.className = cls;
      if (attrs) for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
      return node;
    };
    const btn = (cls: string, icon: IconName, label: string): HTMLButtonElement => {
      const b = el('button', cls, { type: 'button', 'aria-label': label, title: label });
      b.innerHTML = svgIcon(icon);
      return b;
    };

    // Overlay (click = play/pause) + big center button + spinner.
    this.overlay = el('div', 'havik-overlay');
    this.center = el('button', 'havik-center', { type: 'button', 'aria-label': 'Play' });
    this.center.innerHTML = svgIcon('play');
    this.spinner = el('div', 'havik-spinner');
    this.spinner.style.display = 'none';
    this.overlay.append(this.center, this.spinner);

    this.gradient = el('div', 'havik-gradient');

    // Seek bar.
    this.bar = el('div', 'havik-bar');
    this.seek = el('div', 'havik-seek', {
      role: 'slider',
      'aria-label': 'Seek',
      'aria-valuemin': '0',
      'aria-valuemax': '100',
      tabindex: '0',
    });
    const track = el('div', 'havik-seek__track');
    this.seekBuffered = el('div', 'havik-seek__buffered');
    this.seekPlayed = el('div', 'havik-seek__played');
    this.seekThumb = el('div', 'havik-seek__thumb');
    track.append(this.seekBuffered, this.seekPlayed, this.seekThumb);
    this.seek.append(track);

    // Controls row.
    const controls = el('div', 'havik-controls');
    this.playBtn = btn('havik-btn', 'play', 'Play');
    this.muteBtn = btn('havik-btn', 'volumeHigh', 'Mute');
    this.volSlider = el('input', 'havik-vol__slider', {
      type: 'range',
      min: '0',
      max: '1',
      step: '0.05',
      'aria-label': 'Volume',
    });
    const vol = el('div', 'havik-vol');
    vol.append(this.muteBtn, this.volSlider);

    this.live = el('div', 'havik-live');
    this.live.innerHTML = svgIcon('liveDot');
    const liveLabel = this.doc.createElement('span');
    liveLabel.textContent = 'LIVE';
    this.liveGo = el('button', 'havik-live__go', { type: 'button' });
    this.liveGo.textContent = 'Go live';
    this.live.append(liveLabel, this.liveGo);
    this.time = el('div', 'havik-time');
    this.time.textContent = '0:00';
    this.time.style.display = 'none';

    const spacer = el('div', 'havik-spacer');
    this.ccBtn = btn('havik-btn', 'cc', 'Subtitles');
    this.audioBtn = btn('havik-btn', 'settings', 'Audio track');
    this.qualityBtn = btn('havik-btn', 'settings', 'Quality');
    this.pipBtn = btn('havik-btn', 'pip', 'Picture in picture');
    this.fsBtn = btn('havik-btn', 'fullscreen', 'Fullscreen');
    this.ccBtn.style.display = 'none';
    this.audioBtn.style.display = 'none';
    this.qualityBtn.style.display = 'none';
    if (!this.pipSupported()) this.pipBtn.style.display = 'none';

    controls.append(
      this.playBtn,
      vol,
      this.live,
      this.time,
      spacer,
      this.ccBtn,
      this.audioBtn,
      this.qualityBtn,
      this.pipBtn,
      this.fsBtn,
    );
    this.bar.append(this.seek, controls);

    this.menu = el('div', 'havik-menu', { role: 'menu' });
    this.menu.hidden = true;

    this.root.append(this.gradient, this.overlay, this.bar, this.menu);
    if (o.logoUrl) {
      const wm = el('div', 'havik-watermark');
      wm.style.backgroundImage = `url("${o.logoUrl}")`;
      this.root.append(wm);
    }

    this.wire();
    this.syncPlayPause();
    this.syncVolume();
    this.scheduleHide();
  }

  // ---- wiring -------------------------------------------------------------

  private on<T extends EventTarget>(
    target: T,
    type: string,
    handler: EventListenerOrEventListenerObject,
    options?: AddEventListenerOptions,
  ): void {
    target.addEventListener(type, handler, options);
    this.offFns.push(() => target.removeEventListener(type, handler, options));
  }

  private wire(): void {
    const v = this.video;

    // Buttons → player.
    this.on(this.playBtn, 'click', () => this.togglePlay());
    this.on(this.center, 'click', () => this.togglePlay());
    this.on(this.overlay, 'click', (e) => {
      if (e.target === this.overlay) this.togglePlay();
    });
    this.on(this.muteBtn, 'click', () => this.player.setMuted(!v.muted));
    this.on(this.volSlider, 'input', () => {
      const value = Number(this.volSlider.value);
      // Paint the fill from the input event rather than waiting for the
      // 'volumechange' round-trip, so it tracks the thumb during a drag.
      this.setVolumeFill(value);
      this.player.setVolume(value);
      if (value > 0 && v.muted) this.player.setMuted(false);
    });
    this.on(this.liveGo, 'click', () => this.player.seekToLive());
    this.on(this.pipBtn, 'click', () => void this.togglePip());
    this.on(this.fsBtn, 'click', () => void this.toggleFullscreen());
    this.on(this.ccBtn, 'click', () => this.toggleMenu('cc'));
    this.on(this.audioBtn, 'click', () => this.toggleMenu('audio'));
    this.on(this.qualityBtn, 'click', () => this.toggleMenu('quality'));

    // Seek.
    this.on(this.seek, 'pointerdown', (e) => this.onSeekDown(e as PointerEvent));
    this.on(this.seek, 'keydown', (e) => this.onSeekKey(e as KeyboardEvent));

    // Video element → UI.
    this.on(v, 'play', () => this.syncPlayPause());
    this.on(v, 'pause', () => this.syncPlayPause());
    this.on(v, 'volumechange', () => this.syncVolume());
    this.on(v, 'timeupdate', () => this.syncProgress());
    this.on(v, 'progress', () => this.syncProgress());
    this.on(v, 'durationchange', () => this.syncProgress());

    // Player events → UI.
    this.offFns.push(this.player.on('statechange', () => this.syncState()));
    // `ended` can fire a second time with a refined reason (interrupted →
    // match_ended) WITHOUT a state transition, so statechange alone would
    // never repaint the card and the viewer would keep being promised a
    // reconnect for a match that is over. syncState re-reads player.state
    // (still 'ended') and the copy-aware dedup does the rest.
    this.offFns.push(this.player.on('ended', () => this.syncState()));
    this.offFns.push(this.player.on('ready', () => this.refreshTracks()));
    this.offFns.push(this.player.on('qualitychange', () => this.onTracksChanged()));
    this.offFns.push(this.player.on('audiotrackchange', () => this.onTracksChanged()));
    this.offFns.push(this.player.on('texttrackchange', () => this.onTracksChanged()));
    this.offFns.push(this.player.on('pipchange', (active) => this.syncPip(active)));
    this.offFns.push(this.player.on('stats', () => this.syncLive()));
    this.offFns.push(
      this.player.on('error', (err) => {
        this.lastError = err;
        this.statusKind = null; // force the error card to re-render with this error
        this.syncState();
      }),
    );

    // Auto-hide + keyboard.
    this.on(this.root, 'pointermove', () => this.poke());
    this.on(this.root, 'pointerleave', () => this.scheduleHide(0));
    this.on(this.root, 'focusin', () => this.poke());
    this.on(this.root, 'keydown', (e) => this.onKey(e as KeyboardEvent));
    this.on(this.doc, 'pointerdown', (e) => this.onDocPointer(e), { capture: true });
    this.on(this.doc, 'fullscreenchange', () => this.syncFullscreen());
    // Prefixed/native-fullscreen state changes: older Safari fires the
    // prefixed document event for element fullscreen, and iPhone Safari's
    // video-native fullscreen (webkitEnterFullscreen, see toggleFullscreen)
    // reports only through these video-scoped events.
    this.on(this.doc, 'webkitfullscreenchange', () => this.syncFullscreen());
    this.on(v, 'webkitbeginfullscreen', () => this.syncFullscreen());
    this.on(v, 'webkitendfullscreen', () => this.syncFullscreen());

    this.syncState();
  }

  // ---- play / pause -------------------------------------------------------

  private togglePlay(): void {
    if (this.video.paused || this.video.ended) void this.player.play();
    else this.player.pause();
  }

  private syncPlayPause(): void {
    const playing = !this.video.paused && !this.video.ended;
    this.setIcon(this.playBtn, playing ? 'pause' : 'play');
    this.playBtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    this.setIcon(this.center, playing ? 'pause' : 'play');
    // A status card (pre-play / ended / error) owns the CTA, so keep the
    // mid-playback center toggle hidden while one is shown.
    this.center.classList.toggle('havik-gone', playing || !!this.statusBox);
    if (playing) this.scheduleHide();
    else this.show();
  }

  private syncState(): void {
    const state = this.player.state;
    if (state === 'playing' || state === 'buffering') this.hasPlayed = true;
    this.syncStatus(state);
    // Spinner only while genuinely buffering with no card up (the card owns the
    // visual otherwise); the center play/pause button is hidden whenever a card
    // is shown (the card carries its own CTA) or while busy.
    const busy = state === 'loading' || state === 'buffering';
    this.spinner.style.display = busy && !this.statusBox ? '' : 'none';
    if (busy || this.statusBox) this.center.classList.add('havik-gone');
    this.syncLive();
  }

  /** Map the player state to a branded status card (ended / fatal error /
   *  pre-play / armed-waiting), or none. Disabled via `statusOverlays: false`. */
  private syncStatus(state: PlayerState): void {
    let kind: ControlBar['statusKind'] = null;
    if (this.statusOverlays) {
      if (state === 'ended') kind = 'ended';
      else if (state === 'error') kind = 'error';
      else if (state === 'waiting') kind = 'waiting';
      else if (!this.hasPlayed && (state === 'idle' || state === 'paused')) kind = 'preplay';
    }
    this.renderStatus(kind);
  }

  /** Copy for a card kind. Split out of renderStatus so the dedup below can
   *  compare the TEXT, not just the kind — an "ended" card can change its
   *  message without changing kind (interrupted → match_ended). */
  private statusMessageFor(kind: ControlBar['statusKind']): string | null {
    if (kind === 'ended') {
      // An "interrupted" end is a live match whose stream dropped. With
      // auto-rejoin on (the default) the player keeps its live-state
      // subscription open and re-attaches when the bridge returns, so the
      // default copy promises exactly what it will do; a host that disabled
      // rejoin overrides interruptedMessage (see the option's docs).
      return this.player.endedReason === 'interrupted'
        ? this.interruptedMessage
        : this.endedMessage;
    }
    if (kind === 'waiting') {
      // A waiting card after playback has run is a mid-session rejoin (ingest
      // outage → session recreated), not a pre-kickoff wait — "Starting soon"
      // would misdescribe a live match recovering.
      return this.hasPlayed ? 'Reconnecting — please stand by' : 'Starting soon…';
    }
    if (kind === 'error') {
      return this.errorMessage ?? this.lastError?.message ?? 'Playback unavailable';
    }
    return null; // preplay carries a CTA, not copy; null = no card
  }

  /** Build (or tear down) the status card for `kind`. No-op when neither the
   *  kind NOR its copy changed, so repeated statechange events don't
   *  rebuild/flicker the DOM — but a same-kind message change (the
   *  interrupted → match_ended upgrade) still re-renders, otherwise a viewer
   *  keeps being promised a reconnect for a match that is over. */
  private renderStatus(kind: ControlBar['statusKind']): void {
    const msgText = this.statusMessageFor(kind);
    if (kind === this.statusKind && msgText === this.statusMsg) return;
    this.statusKind = kind;
    this.statusMsg = msgText;
    this.statusBox?.remove();
    this.statusBox = null;
    // The card carries its own logo, so the corner watermark is redundant
    // (and reads as a duplicate) while one is up.
    this.root.classList.toggle('havik-has-status', !!kind);
    if (!kind) return;

    const box = this.doc.createElement('div');
    box.className = `havik-status havik-status--${kind}`;
    if (this.logoUrl) {
      const logo = this.doc.createElement('div');
      logo.className = 'havik-status__logo';
      box.append(logo);
    }

    if (kind === 'preplay') {
      // Poster shows through a light scrim; a single branded play CTA.
      const play = this.doc.createElement('button');
      play.type = 'button';
      play.className = 'havik-status__btn havik-status__btn--play';
      play.setAttribute('aria-label', 'Play');
      play.innerHTML = svgIcon('play');
      play.addEventListener('click', () => void this.player.play());
      box.append(play);
    } else {
      const msg = this.doc.createElement('div');
      msg.className = 'havik-status__msg';
      msg.textContent = msgText ?? '';
      box.append(msg);
      // Retry only when re-resolving can plausibly succeed (terminal errors
      // would just fail again).
      if (
        kind === 'error' &&
        this.lastError &&
        !NON_RETRYABLE_ERROR_CODES.has(this.lastError.code)
      ) {
        const retry = this.doc.createElement('button');
        retry.type = 'button';
        retry.className = 'havik-status__btn';
        retry.innerHTML = `${svgIcon('retry')}<span>Retry</span>`;
        retry.addEventListener('click', () => void this.player.retry());
        box.append(retry);
      }
    }

    this.root.append(box);
    this.statusBox = box;
    this.show();
  }

  // ---- volume -------------------------------------------------------------

  private syncVolume(): void {
    const v = this.video;
    const muted = v.muted || v.volume === 0;
    this.setIcon(this.muteBtn, muted ? 'volumeMute' : 'volumeHigh');
    this.muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
    const level = muted ? 0 : v.volume;
    this.volSlider.value = String(level);
    this.setVolumeFill(level);
  }

  /** Drives the accent fill on the volume track (see --havik-vol in styles). */
  private setVolumeFill(level: number): void {
    this.volSlider.style.setProperty('--havik-vol', String(Math.min(1, Math.max(0, level))));
  }

  // ---- progress / live ----------------------------------------------------

  /**
   * The span the scrub bar maps onto, and the window a seek may land in.
   *
   * On a live stream the right end is the live-edge TARGET, not `seekable.end()`
   * — the bleeding edge, where the newest parts aren't published yet and
   * playback stutters frame-by-frame (see the over-seek clamp in the hls.js
   * engine for why hls.js can't recover from it). Mapping 100% to the target
   * means dragging to the far right is "go live", the same spot seekToLive()
   * uses, and the thumb reads full at the live edge instead of parking ~2s short
   * of an end the player deliberately never reaches.
   *
   * `targetLatencySeconds` is engine-reported and hls.js-only, so the native
   * (Safari) path keeps the raw range — AVPlayer runs its own live-edge policy.
   */
  private seekableRange(): { start: number; end: number } | null {
    const v = this.video;
    let start: number;
    let end: number;
    if (v.seekable && v.seekable.length > 0) {
      start = v.seekable.start(0);
      end = v.seekable.end(v.seekable.length - 1);
    } else if (Number.isFinite(v.duration) && v.duration > 0) {
      start = 0;
      end = v.duration;
    } else {
      return null;
    }
    const stats = this.player.getStats();
    // isLive !== false = live or not yet known; targetLatencySeconds is only
    // reported once level details exist, i.e. once isLive is actually known, so
    // VOD never pulls the end back.
    if (stats.isLive !== false && stats.targetLatencySeconds && stats.targetLatencySeconds > 0) {
      end = Math.max(start, end - stats.targetLatencySeconds);
    }
    if (end > start) return { start, end };
    return null;
  }

  private syncProgress(): void {
    if (this.scrubbing) return;
    const range = this.seekableRange();
    if (!range) {
      this.seekPlayed.style.width = '100%';
      return;
    }
    const span = range.end - range.start;
    const played = Math.min(1, Math.max(0, (this.video.currentTime - range.start) / span));
    this.seekPlayed.style.width = `${(played * 100).toFixed(2)}%`;
    this.seekThumb.style.left = `${(played * 100).toFixed(2)}%`;
    this.seek.setAttribute('aria-valuenow', String(Math.round(played * 100)));

    const b = this.video.buffered;
    if (b && b.length > 0) {
      const end = b.end(b.length - 1);
      const buf = Math.min(1, Math.max(0, (end - range.start) / span));
      this.seekBuffered.style.width = `${(buf * 100).toFixed(2)}%`;
    }

    // Time readout only matters for non-live (finite, non-DVR) content.
    const stats = this.player.getStats();
    if (!stats.isLive && Number.isFinite(this.video.duration)) {
      this.time.textContent = `${clock(this.video.currentTime)} / ${clock(this.video.duration)}`;
    }
  }

  private syncLive(): void {
    const stats = this.player.getStats();
    const isLive = stats.isLive !== false; // default to live (these are live streams)
    this.live.style.display = isLive ? '' : 'none';
    this.time.style.display = isLive ? 'none' : '';
    this.live.classList.toggle('is-live', stats.atLiveEdge !== false && isLive);
  }

  // ---- seeking ------------------------------------------------------------

  private onSeekDown(e: PointerEvent): void {
    const range = this.seekableRange();
    if (!range) return;
    e.preventDefault();
    this.scrubbing = true;
    const move = (ev: PointerEvent) => this.seekToPointer(ev, range);
    const up = () => {
      this.scrubbing = false;
      this.doc.removeEventListener('pointermove', move);
      this.doc.removeEventListener('pointerup', up);
    };
    this.doc.addEventListener('pointermove', move);
    this.doc.addEventListener('pointerup', up);
    this.seekToPointer(e, range);
  }

  private seekToPointer(e: PointerEvent, range: { start: number; end: number }): void {
    const rect = this.seek.getBoundingClientRect();
    const pct = rect.width > 0 ? Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) : 0;
    this.seekPlayed.style.width = `${(pct * 100).toFixed(2)}%`;
    this.seekThumb.style.left = `${(pct * 100).toFixed(2)}%`;
    this.video.currentTime = range.start + pct * (range.end - range.start);
  }

  private onSeekKey(e: KeyboardEvent): void {
    if (e.key === 'ArrowLeft') {
      this.nudge(-5);
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      this.nudge(5);
      e.preventDefault();
    }
  }

  private nudge(seconds: number): void {
    const range = this.seekableRange();
    if (!range) return;
    this.video.currentTime = Math.min(
      range.end,
      Math.max(range.start, this.video.currentTime + seconds),
    );
  }

  // ---- menus --------------------------------------------------------------

  private toggleMenu(kind: MenuKind): void {
    if (this.openMenu === kind) {
      this.closeMenu();
      return;
    }
    this.openMenu = kind;
    this.renderMenu(kind);
    this.menu.hidden = false;
    this.show();
  }

  private closeMenu(): void {
    this.openMenu = null;
    this.menu.hidden = true;
    this.menu.replaceChildren();
  }

  private renderMenu(kind: MenuKind): void {
    if (kind === 'quality') this.renderQualityMenu();
    else if (kind === 'audio') this.renderAudioMenu();
    else this.renderCcMenu();
  }

  private menuItem(label: string, checked: boolean, onSelect: () => void): HTMLButtonElement {
    const item = this.doc.createElement('button');
    item.type = 'button';
    item.className = 'havik-menu__item';
    item.setAttribute('role', 'menuitemradio');
    item.setAttribute('aria-checked', checked ? 'true' : 'false');
    const text = this.doc.createElement('span');
    text.textContent = label;
    item.append(text);
    item.addEventListener('click', () => {
      onSelect();
      this.closeMenu();
    });
    return item;
  }

  private renderQualityMenu(): void {
    const levels = this.player.getQualityLevels();
    const current = this.player.getCurrentQuality();
    const title = this.doc.createElement('div');
    title.className = 'havik-menu__title';
    title.textContent = 'Quality';
    this.menu.replaceChildren(
      title,
      this.menuItem('Auto', current === -1, () => this.player.setQuality('auto')),
    );
    for (const lvl of levels) {
      this.menu.append(
        this.menuItem(qualityLabel(lvl), current === lvl.index, () =>
          this.player.setQuality(lvl.index),
        ),
      );
    }
  }

  private renderAudioMenu(): void {
    const tracks = this.player.getAudioTracks();
    const title = this.doc.createElement('div');
    title.className = 'havik-menu__title';
    title.textContent = 'Audio';
    this.menu.replaceChildren(title);
    for (const t of tracks) {
      this.menu.append(
        this.menuItem(audioLabel(t), t.default, () => this.player.setAudioTrack(t.id)),
      );
    }
  }

  private renderCcMenu(): void {
    const tracks = this.player.getTextTracks();
    const title = this.doc.createElement('div');
    title.className = 'havik-menu__title';
    title.textContent = 'Subtitles';
    this.menu.replaceChildren(
      title,
      this.menuItem('Off', false, () => this.player.setTextTrack(-1)),
    );
    for (const t of tracks) {
      this.menu.append(this.menuItem(textLabel(t), false, () => this.player.setTextTrack(t.id)));
    }
  }

  private onTracksChanged(): void {
    if (this.openMenu) this.renderMenu(this.openMenu);
  }

  private refreshTracks(): void {
    const quality = this.player.getQualityLevels();
    const audio = this.player.getAudioTracks();
    const text = this.player.getTextTracks();
    this.qualityBtn.style.display = quality.length > 1 ? '' : 'none';
    this.audioBtn.style.display = audio.length > 1 ? '' : 'none';
    this.ccBtn.style.display = text.length > 0 ? '' : 'none';
    this.onTracksChanged();
  }

  // ---- pip / fullscreen ---------------------------------------------------

  private pipSupported(): boolean {
    return (
      'pictureInPictureEnabled' in this.doc &&
      typeof (this.video as unknown as { requestPictureInPicture?: unknown })
        .requestPictureInPicture === 'function'
    );
  }

  private async togglePip(): Promise<void> {
    const doc = this.doc as Document & { pictureInPictureElement?: Element | null };
    if (doc.pictureInPictureElement === this.video) await this.player.exitPictureInPicture();
    else await this.player.enterPictureInPicture();
  }

  private syncPip(active: boolean): void {
    this.pipBtn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  /**
   * Delegates to the shared capability ladder (core/fullscreen.ts): element
   * fullscreen on the player root where the API exists (keeps this control
   * bar on screen), video-native webkitEnterFullscreen on iPhone (NATIVE
   * controls; the user exits via the native Done button and
   * webkitendfullscreen re-syncs the icon).
   */
  private async toggleFullscreen(): Promise<void> {
    try {
      if (videoFullscreenActive(this.video, this.root)) {
        await exitVideoFullscreen(this.video, this.root);
      } else {
        await enterVideoFullscreen(this.video, this.root);
      }
    } catch {
      // fullscreen can be blocked by policy; ignore
    }
  }

  private syncFullscreen(): void {
    const full = videoFullscreenActive(this.video, this.root);
    this.setIcon(this.fsBtn, full ? 'fullscreenExit' : 'fullscreen');
    this.fsBtn.setAttribute('aria-label', full ? 'Exit fullscreen' : 'Fullscreen');
  }

  // ---- auto-hide ----------------------------------------------------------

  private poke(): void {
    this.show();
    this.scheduleHide();
  }

  private show(): void {
    this.root.classList.remove('havik-hide');
  }

  private scheduleHide(delay = this.autoHideMs): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      const playing = !this.video.paused && !this.video.ended;
      if (playing && !this.openMenu && !this.statusBox) this.root.classList.add('havik-hide');
    }, delay);
  }

  private onDocPointer(e: Event): void {
    if (this.openMenu && !this.menu.contains(e.target as Node)) {
      // a click on the toggle button is handled by its own handler
      const onToggle = [this.qualityBtn, this.audioBtn, this.ccBtn].some((b) =>
        b.contains(e.target as Node),
      );
      if (!onToggle) this.closeMenu();
    }
  }

  // ---- keyboard -----------------------------------------------------------

  private onKey(e: KeyboardEvent): void {
    const target = e.target as HTMLElement | null;
    if (target && target !== this.root && target !== this.seek && target.tagName === 'INPUT')
      return;
    switch (e.key) {
      case ' ':
      case 'k':
        this.togglePlay();
        break;
      case 'm':
        this.player.setMuted(!this.video.muted);
        break;
      case 'f':
        void this.toggleFullscreen();
        break;
      case 'c':
        if (this.ccBtn.style.display !== 'none') this.toggleMenu('cc');
        break;
      case 'l':
        this.player.seekToLive();
        break;
      case 'ArrowUp':
        this.player.setVolume(Math.min(1, this.video.volume + 0.1));
        break;
      case 'ArrowDown':
        this.player.setVolume(Math.max(0, this.video.volume - 0.1));
        break;
      case 'ArrowLeft':
        this.nudge(-5);
        break;
      case 'ArrowRight':
        this.nudge(5);
        break;
      default:
        return;
    }
    this.poke();
    e.preventDefault();
  }

  // ---- helpers ------------------------------------------------------------

  private setIcon(btnEl: HTMLElement, icon: IconName): void {
    btnEl.innerHTML = svgIcon(icon);
  }

  destroy(): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    for (const off of this.offFns) {
      try {
        off();
      } catch {
        // best effort
      }
    }
    this.offFns.length = 0;
    this.closeMenu();
    this.statusBox?.remove();
    for (const node of [this.overlay, this.gradient, this.bar, this.menu]) node.remove();
    this.root.classList.remove('havik-player', 'havik-hide', 'havik-has-status');
  }
}

function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const s = Math.floor(seconds % 60);
  const m = Math.floor((seconds / 60) % 60);
  const h = Math.floor(seconds / 3600);
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function qualityLabel(lvl: QualityLevel): string {
  if (lvl.height) return `${lvl.height}p`;
  return `${Math.round(lvl.bitrate / 1000)} kbps`;
}

function audioLabel(t: AudioTrackInfo): string {
  return t.name || t.lang || `Track ${t.id}`;
}

function textLabel(t: TextTrackInfo): string {
  return t.name || t.lang || `Track ${t.id}`;
}
